import React, { useState } from 'react'
import {
  Controller,
  FieldErrorsImpl,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import {
  Box,
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons'
import get from 'lodash/get'

import { BooleanSelect } from '@/components/common/BooleanSelect'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { FormWarnings } from '@/components/Event/NewScript/components/common/forms/FormWarnings'
import { useAttendeeWarnings } from '@/components/Event/NewScript/hooks/form/useAttendeeWarnings'
import { useInputWarnings } from '@/components/Event/NewScript/hooks/form/useInputWarningStyle'
import {
  getFieldName,
  getSelectableAttendeeRoles,
  getSiblingsPath,
  isWitnessRoleLocked,
  omitIdentityDocumentErrors,
  omitWitnessLocationErrors,
  transformToZoomParticipants,
  WitnessRoleMode,
} from '@/components/Event/NewScript/utils/attendees'
import { ATTENDEE_ROLE_LABEL, AttendeeRole, EventType } from '@/constants'
import { ScriptAttendee, ZoomParticipant } from '@/types'
import { STATES } from '@/utils/states'
import { formatTimeInTimezone } from '@/utils/time'

type Props = {
  zoomParticipants: ZoomParticipant[]
  namePrefix?: string
  timezone: string
  eventType: EventType
  isWitnessLocationEnabled: boolean
  witnessRole?: WitnessRoleMode
  /**
   * The attendees this one is being edited alongside, for the cross-attendee
   * warnings (duplicate names, more than one digital reporter or witness).
   * Only read when there is no namePrefix, because a prefixed attendee sits in
   * an array on the enclosing form and that array is the live set; this is what
   * lets the form work standalone, where no such array exists.
   */
  allAttendees?: ScriptAttendee[]
}

export const AttendeeForm = ({
  zoomParticipants,
  namePrefix,
  timezone,
  eventType,
  isWitnessLocationEnabled,
  witnessRole = 'selectable',
  allAttendees,
}: Props) => {
  const { register, control, trigger, getValues, setValue, formState } =
    useFormContext()
  const attendeeData = useWatch({ control })
  const isPresent = useWatch({
    control,
    name: getFieldName('present', namePrefix),
  })
  const role = useWatch({ control, name: getFieldName('role', namePrefix) })

  // Without a namePrefix the whole form is this one attendee, so its errors are
  // the form's errors.
  const fullError = (
    namePrefix !== undefined
      ? control.getFieldState(namePrefix, formState).error
      : formState.errors
  ) as FieldErrorsImpl<ScriptAttendee> | undefined

  // Filter out identity_document errors - they are validated separately in IdentityDocumentInput
  // City and state go the same way where this form does not render them.
  const error = omitWitnessLocationErrors(
    omitIdentityDocumentErrors(fullError),
    isWitnessLocationEnabled,
  )

  // The enclosing form holds the live set, and the warnings have to read it
  // there: against a stored copy, a name typed into one attendee is not seen
  // colliding with another until after a save.
  const siblingsPath =
    namePrefix !== undefined ? getSiblingsPath(namePrefix) : undefined
  const siblingsValue =
    siblingsPath !== undefined ? get(attendeeData, siblingsPath) : undefined
  const siblings = Array.isArray(siblingsValue)
    ? (siblingsValue as ScriptAttendee[])
    : undefined

  const { warnings, getWarningsMessages } = useAttendeeWarnings(
    namePrefix !== undefined
      ? get(attendeeData, namePrefix)
      : (attendeeData as ScriptAttendee),
    siblings ?? allAttendees,
  )

  const { getInputWarningProps } = useInputWarnings(warnings)
  const [selectedZoomParticipants, setSelectedZoomParticipants] = useState<
    string[]
  >(() => {
    // get current participants mapping
    const attendeeZoomParticipants = getValues(
      getFieldName('zoom_participants', namePrefix),
    )
    return attendeeZoomParticipants.map((zp: ZoomParticipant) => zp.id)
  })

  const attendeeRoles = getSelectableAttendeeRoles({
    witnessRole,
    currentRole: role,
    eventType,
  })
  const isRoleLocked = isWitnessRoleLocked({ witnessRole, currentRole: role })

  return (
    <Stack align="stretch">
      <Group position="center" grow>
        <TextInput
          label="Name"
          withAsterisk
          {...register(getFieldName('name', namePrefix))}
          error={!!error?.name?.message}
          {...getInputWarningProps('name')}
        />
        <TextInput
          label="Email"
          {...register(getFieldName('email', namePrefix))}
          error={!!error?.email?.message}
          {...getInputWarningProps('email')}
        />
      </Group>
      <Group position="center" grow>
        <Controller
          control={control}
          name={getFieldName('role', namePrefix)}
          render={({ field }) => (
            <Tooltip
              label="A witness' role can't be changed here. Witnesses can be managed in the Attendance step."
              multiline
              width={280}
              disabled={!isRoleLocked}
              events={{ hover: true, focus: true, touch: true }}
            >
              <Box>
                <Select
                  {...field}
                  label="Role"
                  placeholder="Select option"
                  error={!!error?.role?.message}
                  onChange={value => {
                    if (!value) {
                      return
                    }
                    setValue(getFieldName('city', namePrefix), null)
                    setValue(getFieldName('state', namePrefix), null)
                    field.onChange(value)
                  }}
                  data={attendeeRoles.map(e => ({
                    value: e,
                    label: ATTENDEE_ROLE_LABEL[e],
                  }))}
                  withAsterisk
                  withinPortal
                  disabled={isRoleLocked}
                  {...getInputWarningProps('role')}
                />
              </Box>
            </Tooltip>
          )}
        />
        <Controller
          control={control}
          name={getFieldName('present', namePrefix)}
          render={({ field: { onChange, value } }) => (
            <BooleanSelect
              withAsterisk
              value={value}
              onChange={value => {
                onChange(value)
                if (value === false) {
                  trigger(getFieldName('zoom_participants', namePrefix))
                }
              }}
              placeholder="Select option"
              label="Joined Zoom"
              error={!!error?.present?.message}
              {...getInputWarningProps('present')}
            />
          )}
        />
      </Group>
      {isPresent && !!zoomParticipants.length && (
        <>
          <Stack spacing={4}>
            <Group spacing={4}>
              <Text size="sm" fw={500}>
                Zoom account name before renaming
                <Text size="sm" component="span" color="red">
                  {' '}
                  *
                </Text>
              </Text>
              <Tooltip
                label="Select the original Zoom display name for this attendee. If they rejoined the call, select each entry. If two attendees joined from the same location, select the same Zoom account for both."
                multiline
                width={400}
              >
                <QuestionMarkCircledIcon />
              </Tooltip>
            </Group>
            <Controller
              control={control}
              name={getFieldName(`zoom_participants`, namePrefix)}
              render={({ field: { onChange } }) => (
                <MultiSelect
                  data={zoomParticipants.map(zp => ({
                    label: `${zp.user_name} (${formatTimeInTimezone(
                      zp.join_time,
                      timezone,
                    )})`,
                    value: zp.id,
                  }))}
                  placeholder="Select option"
                  searchable
                  value={selectedZoomParticipants}
                  onChange={(value: string[]) => {
                    const newZoomParticipants = transformToZoomParticipants(
                      value,
                      zoomParticipants,
                    )
                    onChange(newZoomParticipants)
                    setSelectedZoomParticipants(value)
                  }}
                  error={!!error?.zoom_participants?.message}
                />
              )}
            />
          </Stack>
          <Box
            py="sm"
            px="md"
            w="100%"
            sx={theme => ({
              // TODO: Add this color to theme
              backgroundColor: '#CCD6FF',
              borderRadius: theme.radius['sm'],
            })}
          >
            <Text fw={600} color="gray.9" size="sm">
              Confirm that you have renamed attendee in Zoom to reflect a clear
              identity (ie first and last name)
            </Text>
          </Box>
        </>
      )}
      {isWitnessLocationEnabled && role === AttendeeRole.WITNESS && (
        <Group position="center" grow>
          <TextInput
            label="City"
            withAsterisk
            {...register(getFieldName('city', namePrefix))}
            error={!!error?.city?.message}
            {...getInputWarningProps('city')}
          />
          <Controller
            control={control}
            name={getFieldName('state', namePrefix)}
            render={({ field }) => (
              <Select
                {...field}
                error={!!error?.state?.message}
                label="State"
                withAsterisk
                placeholder="Select state"
                searchable
                data={Object.keys(STATES).map(key => ({
                  value: key,
                  label: STATES[key],
                }))}
                {...getInputWarningProps('state')}
              />
            )}
          />
        </Group>
      )}
      <FormErrors<ScriptAttendee> errors={error} />
      <FormWarnings warnings={getWarningsMessages()} />
    </Stack>
  )
}
